"use client";

import { Category, ProductData } from "@/lib/5874/types";
import clsx from "clsx";
import React, { FC, useMemo } from "react";
import styles from "./ProductListItem.module.scss";
import { ProductForm } from "@/components/PDP/ProductForm/ProductForm";
import StockAvailability from "@/components/StockAvailability/StockAvailability";
import { ProductContextProvider } from "@/providers/ProductContextProvider";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import Pricing from "@/components/ProductCard/Pricing/Pricing";
import Link from "next/link";
import useStoreKeys from "@/hooks/useStoreKeys";
import { useDataLayerContext } from "@/datalayer/DataLayerContextProvider";
import { ClickData } from "@/lib/searchspring/tracker";
import useIntelliSuggestTracking from "@/hooks/useIntelliSuggestTracking";
import mapProductOptions from "@/hooks/mapProductOptions";
import { userHasRequiredCertifications } from "@/hooks/userHasRequiredCertifications";
import { useBigCommerceStoreContext } from "@/providers/BigCommerceStoreProvider";

export interface ProductListItemProps {
	product: ProductData;
	isInCart?: boolean;
	onClick?: (clickData: ClickData) => void;
	category?: Category;
	currencyCode: string;
}

const ProductListItem: FC<ProductListItemProps> = ({
	product,
	isInCart,
	category,
	currencyCode,
}) => {
	const { session } = useSessionContext();
	const { store, lang } = useStoreKeys();
	const { name, pricesExTax, path, brand, inventory, sku, id, categories } =
		product;
	const isCustomer = session !== null;

	// Product Certification Restrictions
	const { companyCourseCertification } = useBigCommerceStoreContext();
	const hasRequiredCertifications = userHasRequiredCertifications(
		store,
		product,
		companyCourseCertification,
	);
	const showAddToCart =
		isCustomer && hasRequiredCertifications && product.inventory.isInStock;

	// const showAddToCart = product.inventory.isInStock;

	// Datalayer
	const { selectItem } = useDataLayerContext();

	// Intellisuggest
	const { productClick } = useIntelliSuggestTracking();

	const productSpecifications = useMemo(() => {
		return mapProductOptions([product]);
	}, [product]);

	return (
		<ProductContextProvider product={product}>
			<div className={clsx([styles.item, isInCart ? styles.inCart : null])}>
				<div className={styles.detail}>
					{brand && (
						<Link href={brand?.path} className={styles.brandLink}>
							{brand?.name}
						</Link>
					)}
					{sku && <span className={styles.sku}>{sku}</span>}
					<Link
						onClick={(e) => {
							productClick(product);
							selectItem(
								product,
								id,
								String(category?.id),
								category?.name,
								currencyCode,
							);
						}}
						href={`/${store}/${lang}${path}`}
						className={styles.name}
					>
						{name}
					</Link>
					{productSpecifications && productSpecifications.length > 0 && (
						<div className={styles.specs}>
							{productSpecifications?.map((spec) => (
								<div key={spec.label} className={styles.spec}>
									<span className={styles.label}>{spec.label}:</span>
									<span className={styles.value}>{spec.options[0].label}</span>
								</div>
							))}
						</div>
					)}
					<span className={styles.price}>
						<Pricing linkClassName={styles.loginLink} />
					</span>
				</div>
				<div className={styles.actions}>
					{isCustomer ? (
						<>
							<ProductForm showAddToCart={showAddToCart} />
							<StockAvailability product={product} />
						</>
					) : null}
				</div>
			</div>
		</ProductContextProvider>
	);
};

export default ProductListItem;
